<template>
  <div class="map"> <!-- v-show="!loaded" -->
    <div v-show="!loaded || !reloaded" class="text-center loader">
      <font-awesome-icon icon="spinner" spin />
    </div>
    <div v-show="loaded">
      <h1 class="text-center">United States Weather</h1>
      <p>You can use the following visualization to see changes through time
        in American weather. You can view the average temperature,
        maximum average temperature, minimum average temperature and average precipitation level
         for every county in the contiguous United States. The map shows data for a given month for the
        lower 48 states in the United States.
        Below the map you can focus in on a specific county to view its weather over time. Data ranges from
        1895 to 2020. Hover over a graph to see details for that county and month.
        (Note: does not include Alaska or Hawaii).</p>
      <h2 class="text-center">{{currentDate}}</h2>
      <v-select placeholder="Select/search for a date"
                v-model="selected_month"
                :options="dateList"
                :clearable="false"
                class="datelist-style"></v-select>
      <div class="text-center">
        <div @click="mapType" class="btns btn-group" role="group" aria-label="Map temperature/precipitation buttons">
          <button id="t" type="button" class="btn btn-secondary"><font-awesome-icon icon="sun" /> Avg Temp</button>
          <button id="h" type="button" class="btn btn-secondary"><font-awesome-icon icon="temperature-high" /> Max Temp</button>
          <button id="l" type="button" class="btn btn-secondary"><font-awesome-icon icon="temperature-low" /> Min Mean</button>
          <button id="p" type="button" class="btn btn-secondary"><font-awesome-icon icon="cloud-showers-heavy" /> Rainfall</button>
        </div>
      </div>
      <div class="map-legend">
        <legend-map :colors="colors" :type="type" :width="width"></legend-map>
      </div>
      <svg :width="width" :height="height" :viewBox="viewBox">
        <g ref="county-list" class="counties">
          <template v-for="(p, index) in paths">
            <path :d="buildPath(p)"
                  :fill="countyColor(p.properties)"
                  :key="index" vector-effect="non-scaling-stroke"
                  :content="infoFormat(p.properties)"
                  v-tippy="{delay: 0, arrow: true}" />
          </template>
        </g>
      </svg>
      <strip-chart-wrapper></strip-chart-wrapper>
    </div>
  </div>
</template>

<script>
  import * as d3 from 'd3';
  import * as topojson from 'topojson-client';
  import debounce from 'lodash.debounce';
  import range from 'lodash.range';
  import legendMap from './legendMap.vue';
  import stripChartWrapper from "./stripChartWrapper";
  import countyHelpers from "../mixins/countyHelpers";

  export default {
    name: 'Map',

    components: {legendMap, stripChartWrapper},

    mixins: [countyHelpers],

    data() {
      return {
        colors: [],
        height: 700,
        map: [],
        counties: [],
        reading_date: '2020-3',
        width: 800,
        scale: {},
        projection: {},
        paths: [],
        selected_month: '',
        station: '01001',
        loaded: false,
        reloaded: true,
        type: 't'
      }
    },

    watch: {
      selected_month(calendar_date) {
        if (calendar_date) {
          this.reloaded = false;
          this.reading_date = calendar_date.full_date;
          let files = [
            d3.json(`static/maps-counties-only/${this.reading_date}.json`)
          ];

          let vm = this;

          Promise.all(files).then((data) => {
            vm.map.objects.counties.geometries = data[0];
            this.paths = topojson.feature(this.map, this.map.objects.counties).features;
            this.reloaded = true;
          });
        }
      }
    },

    computed: {
      currentDate() {
        let formatDate = d3.timeFormat('%B %Y');
        let date_parts = this.reading_date.split('-');
        return formatDate(new Date(+date_parts[0], date_parts[1] - 1));
      },

      viewBox() {
        return `0 0 ${this.width} ${this.height}`;
      },

      dateList() {
        let months = range(1, 13);
        let years = range(1895, 2021);
        let combined = [];

        years.forEach((y) => {
          months.forEach((m) => {
            let text_month = this.monthList(y, m - 1);

            if (y < 2020 || (y === 2020 && m < 4)) {
              combined.unshift({full_date: `${y}-${m}`, label: `${text_month} ${y}`});
            }
          });
        });

        return combined;
      }
    },

    methods: {
      load() {
        const files = [
          d3.json(`static/maps/${this.reading_date}.json`)
        ];

        let vm = this;

        Promise.all(files).then((data) => {
          vm.map = data[0];
          vm.mapScaling();
          vm.counties = data[1];
          this.loaded = true;
        });
      },

      mapType(e) {
        this.type = e.target.id;
        this.colors = this.colorList(this.type);
      },

      buildPath(d) {
        return d3.geoPath().projection(this.projection)(d);
      },

      countyColor(values) {
        return `#${values[this.type]}`;
      },

      infoFormat(county) {
        let state = this.stateCode(county.geoid);
        if (state === undefined) {
          return `<p>${county.name} County
                ${this.weatherType(county)}</p>`;
        }
        return `<h5>${county.name} County, ${state.state}</h5>
                <p>${this.weatherType(county)}</p>`;
      },

      weatherType(d) {
        if (this.type === 't') {
          return `Avg Temp: ${d.tv} degrees`;
        } else if (this.type === 'p') {
          return `Total Rainfall: ${d.pv} inches`;
        } else if (this.type === 'h') {
          return `Max Temp: ${d.hv} degrees`;
        } else {
          return `Min Temp: ${d.lv} degrees`;
        }
      },

      mapScaling () {
        this.width = window.innerWidth - 50;
        this.height = window.innerWidth * .5;

        let scale = 1;
        let path = d3.geoPath().projection(d3.geoAlbersUsa()
          .scale(scale)
          .translate([0,0]));
        let bounds = path.bounds(topojson.mesh(this.map, this.map.objects.counties));

        scale = .95 / Math.max((bounds[1][0] - bounds[0][0]) / this.width, (bounds[1][1] - bounds[0][1]) / this.height);
        let translation = [(this.width - scale * (bounds[1][0] + bounds[0][0])) / 2,
          (this.height - scale * (bounds[1][1] + bounds[0][1])) / 2];

        // update projection
        this.projection = d3.geoAlbersUsa()
          .scale(scale)
          .translate(translation);
        this.paths = topojson.feature(this.map, this.map.objects.counties).features;
      },

      monthList(year, month) {
        const date = new Date(year, month, 1);
        return date.toLocaleString('default', { month: 'long' });
      },

      zoomMap() {
        let svg = d3.select('g.counties');
        let zoom = d3.zoom().scaleExtent([1, 5]).on("zoom", zoomed);
        svg.call(zoom);

        function zoomed() {
          svg.attr('transform', d3.event.transform);
        }
      }
    },

    created() {
      this.load();
    },

    mounted() {
      this.zoomMap();
      this.colors = this.colorList(this.type);
    },

    beforeMount() {
      window.addEventListener('resize', debounce(this.mapScaling, 250));
    },

    beforeDestroy () {
      window.removeEventListener('resize', this.mapScaling);
    }
  }
</script>

<style scoped>
  .loader {
    background-color: #333;
    font-size: 5rem;
    padding: 15px;
    position: absolute;
    top: 50%;
    left:50%;
    transform: translate(-50%,-50%);
    z-index: 100;
  }

  .map-legend {
    height: 50px;
    margin-top: 25px;
  }

  .map-legend svg {
    height: 50px;
  }

  path {
    pointer-events: all;
    stroke: white;
    stroke-opacity: .9;
    stroke-width: 1px;
  }

  path:hover {
    opacity: .8;
  }

  h1 {
    margin-top: 25px;
  }
</style>



// WEBPACK FOOTER //
// src/components/Map.vue